import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faBarcode,
  faIndianRupeeSign,
  faBoxesStacked,
  faPlus,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet";
import { configData } from "../config";
import "./classes/ManageItems.css";

const API_BASE = configData.APP_URL;

const ManageItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Create form state (left panel)
  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    retail_price: "",
    wholesale_price: "",
    stock: "",
  });

  // Edit modal state
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    barcode: "",
    retail_price: "",
    wholesale_price: "",
    stock: "",
  });
  const [modal, setModal] = useState(false);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}items/lists`);
      const data = await res.json();

      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data?.data && Array.isArray(data.data)) {
        list = data.data;
      } else if (data?.items && Array.isArray(data.items)) {
        list = data.items;
      }

      setItems(list);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Item name is required");
      return;
    }

    if (!formData.barcode.trim()) {
      alert("Barcode is required");
      return;
    }

    if (!formData.retail_price.trim()) {
      alert("Retail Price is required");
      return;
    }

    if (!formData.wholesale_price.trim()) {
      alert("Wholesale Price is required");
      return;
    }

    if (!formData.stock.trim()) {
      alert("Stock is required");
      return;
    }

    const numericRetailPrice = parseFloat(formData.retail_price || 0);
    const numericWholeSalePrice = parseFloat(formData.wholesale_price || 0);
    const numericStock = parseInt(formData.stock || 0, 10);

    if ((Number.isNaN(numericRetailPrice) || numericRetailPrice < 0) ||
    (Number.isNaN(numericWholeSalePrice) || numericWholeSalePrice < 0)) {
      alert("Price must be a valid number");
      return;
    }

    if (Number.isNaN(numericStock) || numericStock < 0) {
      alert("Stock must be a valid number");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}items/creates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          barcode: formData.barcode,
          retail_price: numericRetailPrice,
          wholesale_price: numericWholeSalePrice,
          stock: numericStock,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create item");
      alert("Item created successfully");

      setFormData({
        name: "",
        barcode: "",
        retail_price: "",
        wholesale_price: "",
        stock: "",
      });
      fetchItems();
    } catch (error) {
      console.error("Error saving item:", error);
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData.name.trim()) {
      alert("Item name is required");
      return;
    }

    if (!editFormData.barcode.trim()) {
      alert("Barcode is required");
      return;
    }

    if (!editFormData.retail_price.trim()) {
      alert("Retail Price is required");
      return;
    }

    if (!editFormData.wholesale_price.trim()) {
      alert("Wholesale Price is required");
      return;
    }

    if (!editFormData.stock.trim()) {
      alert("Stock is required");
      return;
    }

    const numericRetailPrice = parseFloat(editFormData.retail_price || 0);
    const numericWholeSalePrice = parseFloat(editFormData.wholesale_price || 0);
    const numericStock = parseInt(editFormData.stock || 0, 10);

    if (
      Number.isNaN(numericRetailPrice) ||
      numericRetailPrice < 0 ||
      Number.isNaN(numericWholeSalePrice) ||
      numericWholeSalePrice < 0
    ) {
      alert("Price must be a valid number");
      return;
    }

    if (Number.isNaN(numericStock) || numericStock < 0) {
      alert("Stock must be a valid number");
      return;
    }

    setEditSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}items/updates?item_id=${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editFormData.name,
          barcode: editFormData.barcode,
          retail_price: numericRetailPrice,
          wholesale_price: numericWholeSalePrice,
          stock: numericStock,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update item");
      alert("Item updated successfully");
      setEditingId(null);
      setModal(false);
      setEditFormData({
        name: "",
        barcode: "",
        retail_price: "",
        wholesale_price: "",
        stock: "",
      });
      fetchItems();
    } catch (error) {
      console.error("Error saving item:", error);
      alert(error.message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    const id = item.id ?? item.item_id;
    setEditFormData({
      name: item.name ?? item.item_name ?? "",
      barcode: item.barcode ?? "",
      retail_price:
        item.retail_price != null ? String(item.retail_price) : "",
      wholesale_price:
        item.wholesale_price != null ? String(item.wholesale_price) : "",
      stock:
        item.stock != null
          ? String(item.stock)
          : item.quantity != null
          ? String(item.quantity)
          : "",
    });
    setEditingId(id);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const res = await fetch(`${API_BASE}items/deletes?item_id=${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete item");
      alert("Item deleted successfully");

      fetchItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      alert(error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setModal(false);
    setEditFormData({
      name: "",
      barcode: "",
      retail_price: "",
      wholesale_price: "",
      stock: "",
    });
  };

  return (
    <div className="pos-main manage-items-main">
      <Helmet>
        <title>Manage Items</title>
      </Helmet>

      <div className="main">
        <div className="left-container">
          <span className="create-user-title">
            <FontAwesomeIcon icon={faBox} />
            <h3>Item Management</h3>
          </span>

          <div className="create-user-container">
            <h2>Add New Item</h2>

            <form onSubmit={handleSubmit}>
              <div className="login-form-field">
                <label htmlFor="name">Item Name *</label>
                <div className="login-input-container">
                  <span className="login-input-icon-container">
                    <FontAwesomeIcon icon={faBox} />
                  </span>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Enter item name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="login-form-field">
                <label htmlFor="barcode">Barcode *</label>
                <div className="login-input-container">
                  <span className="login-input-icon-container">
                    <FontAwesomeIcon icon={faBarcode} />
                  </span>
                  <input
                    id="barcode"
                    type="text"
                    name="barcode"
                    placeholder="Enter barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="login-form-field">
                <label htmlFor="retail_price">Retail Price *</label>
                <div className="login-input-container">
                  <span className="login-input-icon-container">
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                  </span>
                  <input
                    id="retail_price"
                    type="number"
                    step="0.01"
                    min="0"
                    name="retail_price"
                    placeholder="Enter retail price"
                    value={formData.retail_price}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="login-form-field">
                <label htmlFor="wholesale_price">Wholesale Price *</label>
                <div className="login-input-container">
                  <span className="login-input-icon-container">
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                  </span>
                  <input
                    id="wholesale_price"
                    type="number"
                    step="0.01"
                    min="0"
                    name="wholesale_price"
                    placeholder="Enter wholesale price"
                    value={formData.wholesale_price}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="login-form-field">
                <label htmlFor="stock">Stock *</label>
                <div className="login-input-container">
                  <span className="login-input-icon-container">
                    <FontAwesomeIcon icon={faBoxesStacked} />
                  </span>
                  <input
                    id="stock"
                    type="number"
                    min="0"
                    name="stock"
                    placeholder="Enter stock quantity"
                    value={formData.stock}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="login-form-submit"
                  disabled={submitting}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {submitting ? " Saving..." : " Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="center left-center">
          <div className="items-container">
            <h3>Existing Items</h3>
            {loading ? (
              <p className="no-data-message">Loading items...</p>
            ) : items.length === 0 ? (
              <p className="no-data-message">No items yet. Add one using the form.</p>
            ) : (
			        <>
                <table className="item-table desktop-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Barcode</th>
                      <th>Retail Price</th>
                      <th>Wholesale Price</th>
                      <th>Stock</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const id = item.id ?? item.item_id;
                      const name = item.name ?? item.item_name ?? "—";
                      const barcode = item.barcode ?? "—";
                      const retailPrice =
                        typeof item.retail_price === "number"
                          ? `₹${item.retail_price.toFixed(2)}`
                          : item.retail_price
                          ? `₹${Number(item.retail_price).toFixed(2)}`
                          : "—";
                      const wholesalePrice =
                        typeof item.wholesale_price === "number"
                          ? `₹${item.wholesale_price.toFixed(2)}`
                          : item.wholesale_price
                          ? `₹${Number(item.wholesale_price).toFixed(2)}`
                          : "—";
                      const stock =
                        item.stock != null
                          ? item.stock
                          : item.quantity != null
                          ? item.quantity
                          : "—";

                      return (
                        <tr key={id}>
                          <td>{name}</td>
                          <td>{barcode}</td>
                          <td>{retailPrice}</td>
                          <td>{wholesalePrice}</td>
                          <td>{stock}</td>
                          <td className="icon-cell manage-items-icon-cell">
                            <span
                              className="action-icon edit"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </span>
                            <span
                              className="action-icon delete"
                              onClick={() => handleDelete(id)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <table className="item-table mobile-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const id = item.id ?? item.item_id;
                      const name = item.name ?? item.item_name ?? "—";
                      const barcode = item.barcode ?? "—";
                      const retailPrice =
                        typeof item.retail_price === "number"
                          ? `₹${item.retail_price.toFixed(2)}`
                          : item.retail_price
                          ? `₹${Number(item.retail_price).toFixed(2)}`
                          : "—";
                      const wholesalePrice =
                        typeof item.wholesale_price === "number"
                          ? `₹${item.wholesale_price.toFixed(2)}`
                          : item.wholesale_price
                          ? `₹${Number(item.wholesale_price).toFixed(2)}`
                          : "—";
                      const stock =
                        item.stock != null
                          ? item.stock
                          : item.quantity != null
                          ? item.quantity
                          : "—";

                      return (
                        <tr key={id}>
                          <td>{name} 
                            <span className="decription">
                              barcode: {barcode} <br />
                              stock: {stock}
                            </span>
                          </td>
                          <td>
                            <span className="decription" style={{fontSize: "12px"}}>
                              Retail: {retailPrice}<br />
                              Wholesale: {wholesalePrice}
                            </span>
                          </td>
                          <td className="icon-cell manage-items-icon-cell">
                            <span
                              className="action-icon edit"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </span>
                            <span
                              className="action-icon delete"
                              onClick={() => handleDelete(id)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
			        </>
			      )}
          </div>
          {modal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h2>Edit Item</h2>
                <form onSubmit={handleEditSubmit}>
                  <table>
                    <tbody>
                      <tr className="login-form-field login-form-field-modal">
                        <td><label htmlFor="edit_name">Item Name *</label></td>
                        <td>
                          <input
                            id="edit_name"
                            type="text"
                            name="name"
                            placeholder="Enter item name"
                            value={editFormData.name}
                            onChange={handleEditChange}
                          />
                        </td>
                      </tr>

                      <tr className="login-form-field login-form-field-modal">
                        <td><label htmlFor="edit_barcode">Barcode *</label></td>
                        <td>
                          <input
                            id="edit_barcode"
                            type="text"
                            name="barcode"
                            placeholder="Enter barcode"
                            value={editFormData.barcode}
                            onChange={handleEditChange}
                          />
                        </td>
                      </tr>

                      <tr className="login-form-field login-form-field-modal">
                        <td><label htmlFor="edit_retail_price">Retail Price *</label></td>
                        <td>
                          <input
                            id="edit_retail_price"
                            type="number"
                            step="0.01"
                            min="0"
                            name="retail_price"
                            placeholder="Enter retail price"
                            value={editFormData.retail_price}
                            onChange={handleEditChange}
                          />
                        </td>
                      </tr>

                      <tr className="login-form-field login-form-field-modal">
                        <td><label htmlFor="edit_wholesale_price">Wholesale Price *</label></td>
                        <td>
                          <input
                            id="edit_wholesale_price"
                            type="number"
                            step="0.01"
                            min="0"
                            name="wholesale_price"
                            placeholder="Enter wholesale price"
                            value={editFormData.wholesale_price}
                            onChange={handleEditChange}
                          />
                        </td>
                      </tr>

                      <tr className="login-form-field login-form-field-modal">
                        <td><label htmlFor="edit_stock">Stock *</label></td>
                        <td>
                          <input
                            id="edit_stock"
                            type="number"
                            min="0"
                            name="stock"
                            placeholder="Enter stock quantity"
                            value={editFormData.stock}
                            onChange={handleEditChange}
                          />
                        </td>
                      </tr>

                      <tr>
                        <td colSpan={2}>
                          <div className="modal-buttons">
                            <button type="submit" disabled={editSubmitting}>
                              {editSubmitting ? "Saving..." : "Update"}
                            </button>
                            <button type="button" onClick={handleCancelEdit}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageItems;